import React, { useCallback, useMemo, useRef } from "react";
import { stylex } from "./stylex";
import { cssVariables } from "./css_variables";
import { Text } from "./text";
import { Icon } from "./icon";
import { Spacing } from "./spacing";

type ToastId = string;
type ToastIntent = "success" | "error" | "warning";

interface ToastSettings {
  /**
   * Duration for how long the toast should stay active.
   * @default 3000
   */
  duration?: number;

  /**
   * Assign an id to the toast so you can remove it later.
   */
  id?: ToastId;

  intent?: ToastIntent;

  message: string;
}

interface ToastInstance extends ToastSettings {
  /**
   * Assign an id to the toast so you can remove it later.
   */
  id: ToastId;

  /**
   * Callback invoked when the duration is up.
   */
  onRemove: () => void;
}

interface ToastContextValue {
  error: (toastSettings: ToastSettings) => ToastInstance;
  notify: (toastSettings: ToastSettings) => ToastInstance;
  success: (toastSettings: ToastSettings) => ToastInstance;
  warning: (toastSettings: ToastSettings) => ToastInstance;
  removeToast: (id: ToastId) => void;
}

const defaultToastInstance: ToastInstance = {
  id: "1",
  onRemove: () => null,
  message: "",
};

const defaultToastContext: ToastContextValue = {
  error: () => defaultToastInstance,
  notify: () => defaultToastInstance,
  removeToast: (id) => {
    return;
  },
  success: () => defaultToastInstance,
  warning: () => defaultToastInstance,
};

const ToastContext = React.createContext(defaultToastContext);

export const useToast = () => {
  return React.useContext(ToastContext);
};

const hasCustomId = (toastSettings: ToastSettings) => !!toastSettings.id;

interface ToastProviderState {
  toasts: ToastInstance[];
}

const initialState: ToastProviderState = {
  toasts: [],
};

enum ActionType {
  ADD_TOAST = "ADD_TOAST",
  REMOVE_TOAST = "REMOVE_TOAST",
}

type Action =
  | { type: ActionType.ADD_TOAST; payload: { toast: ToastInstance } }
  | { type: ActionType.REMOVE_TOAST; payload: { id: ToastId } };

const reducer = (state: ToastProviderState, action: Action) => {
  switch (action.type) {
    case ActionType.ADD_TOAST:
      return { toasts: [...state.toasts, action.payload.toast] };
    case ActionType.REMOVE_TOAST:
      return {
        toasts: state.toasts.filter((toast) => toast.id !== action.payload.id),
      };
    default:
      throw new Error("Invalid action type");
  }
};

interface ToastProviderProps {
  children?: React.ReactNode;
}

export const ToastProvider = (props: ToastProviderProps) => {
  const { children } = props;
  const idCounterRef = React.useRef(0);
  // Use reducer because we want access previous value of state
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const toastsRef = useRef(state.toasts);
  toastsRef.current = state.toasts;

  const createToastInstance = useCallback(
    (toastSettings: ToastSettings): ToastInstance => {
      const uniqueId = ++idCounterRef.current;
      const id = hasCustomId(toastSettings)
        ? `${toastSettings.id}-${uniqueId}`
        : `${uniqueId}`;

      return {
        id,
        onRemove: () =>
          dispatch({ type: ActionType.REMOVE_TOAST, payload: { id } }),
        duration: toastSettings.duration,
        intent: toastSettings.intent,
        message: toastSettings.message,
      };
    },
    []
  );

  const notify = useCallback(
    (toastSettings: ToastSettings) => {
      const toastInstance = createToastInstance(toastSettings);

      // If there's a custom toast ID passed, close existing toasts with the same custom ID
      if (hasCustomId(toastSettings)) {
        for (const toast of toastsRef.current) {
          // Since unique ID is still appended to a custom ID, skip the unique ID and check only prefix
          if (String(toast.id).startsWith(String(toastSettings.id))) {
            dispatch({
              payload: { id: toast.id },
              type: ActionType.REMOVE_TOAST,
            });
          }
        }
      }

      dispatch({
        type: ActionType.ADD_TOAST,
        payload: { toast: toastInstance },
      });

      return toastInstance;
    },
    [dispatch, createToastInstance]
  );

  const contextValue = useMemo(
    () => ({
      error: (toastSettings: ToastSettings) =>
        notify({ ...toastSettings, intent: "error" }),
      notify: (toastSettings: ToastSettings) => notify(toastSettings),
      success: (toastSettings: ToastSettings) =>
        notify({ ...toastSettings, intent: "success" }),
      warning: (toastSettings: ToastSettings) =>
        notify({ ...toastSettings, intent: "warning" }),
      removeToast: (id: ToastId) =>
        dispatch({ type: ActionType.REMOVE_TOAST, payload: { id } }),
    }),
    [dispatch, notify]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div style={styles("root")}>
        {state.toasts.map((toast) => {
          return (
            <Toast
              key={toast.id}
              id={toast.id}
              duration={toast.duration}
              intent={toast.intent}
              message={toast.message}
              onRemove={toast.onRemove}
            />
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

interface ToastProps extends ToastInstance {}

const Toast = (props: ToastProps) => {
  const { onRemove, duration = 5000, intent, message } = props;

  const [offset, setOffset] = React.useState(-500);

  React.useEffect(() => {
    setTimeout(() => {
      setOffset(0);
    }, 0);

    const timer = setTimeout(() => {
      setOffset(-500);

      setTimeout(() => {
        onRemove();
      }, 100);
    }, duration - 100);

    return () => clearTimeout(timer);
  }, [duration, onRemove]);

  return (
    <div
      style={{
        ...styles("toast", intent),
        transform: `translateY(${offset}px)`,
      }}
    >
      {getIconForIntent(intent)}
      <Spacing width={16} />
      <Text color={getTextColorForIntent(intent)}>{message}</Text>
    </div>
  );
};

function getIconForIntent(intent?: ToastIntent) {
  switch (intent) {
    case "error":
      return <Icon name="alert-triangle" color={cssVariables.textColorError} />;
    case "success":
      return <Icon name="check-circle" color={cssVariables.textColorSuccess} />;
    default:
      return null;
  }
}

function getTextColorForIntent(intent?: ToastIntent) {
  switch (intent) {
    case "error":
      return "error";
    case "success":
      return "success";
    default:
      return "default";
  }
}

const styles = stylex.create({
  root: {
    left: 32,
    marginBottom: 0,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 0,
    maxWidth: 560,
    position: "fixed",
    paddingTop: 16,
    right: 32,
    top: 0,
    // Need setting zIndex for toast message higher than the header, which has zIndex of 1030 by default Bootstrap fixed-top class, so that toast message is visible.
    // Need setting zIndex for toast message higher than the modal (stripe), which has zIndex of 1055 by default Bootstrap, so that toast message is visible.
    // https://getbootstrap.com/docs/5.2/layout/z-index/
    zIndex: 1057,
  },
  toast: {
    padding: 16,
    borderRadius: cssVariables.radius,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    position: "relative",
    transition: "0.2s",
    marginBottom: 16,
  },
  error: {
    backgroundColor: "#feecf0",
  },
  success: {
    backgroundColor: "#effaf3",
  },
  warning: {
    backgroundColor: "#fffbeb",
  },
});
